<template>
  <small-modal
    class="chapters-modal"
    :show-close-button="false"
    @close="closeModal"
  >
    <div class="video-editor-modal">
      <div class="video-editor-header">
        <h2 class="title">{{ $t("create.step2.addChaptersModal.title") }}</h2>
        <div class="header-buttons">
          <button class="header-button back" @click="closeModal">
            {{ $t("create.step2.addChaptersModal.back") }}
            <SquaredCross class="squared-cross" />
          </button>
          <button class="header-button save" @click="saveChapters">
            {{ $t("create.step2.addChaptersModal.save") }} <SquaredCheck />
          </button>
        </div>
      </div>
      <div class="video-container">
        <div class="video-wrapper">
          <video
            ref="videoPlayer"
            :src="videoBlobUrl || video.url"
            class="video-player"
            @timeupdate="updateCurrentTime"
            @play="handlePlay"
            @pause="handlePause"
            @ended="handleEnded"
          ></video>
          <div v-if="chapters.length > 0" class="progress-container">
            <div class="chapters-container">
              <div
                v-for="(segment, index) in timelineSegments"
                :key="index"
                class="chapter"
                :style="{
                  width: chapterWidth + '%',
                  marginRight:
                    index === timelineSegments.length - 1 ? '0' : '2px',
                }"
              >
                <div
                  class="chapter-fill"
                  :style="{ width: getChapterFill(segment) + '%' }"
                ></div>
              </div>
            </div>
          </div>
          <button class="mute-button" @click="toggleMute">
            <Unmute v-if="isMuted" />
            <Mute v-else />
          </button>
        </div>
      </div>
      <div class="actions">
        <div class="pack-buttons">
          <button
            v-tippy="{ placement: 'top', arrow: true }"
            :content="'Space bar'"
            @click="togglePlay"
          >
            <PauseIcon v-if="isPlaying" class="play-pause-icon" />
            <PlayIcon v-else class="play-pause-icon" />
          </button>
          <button @click="addChapter">
            <SplitIcon />{{ $t("create.step2.addChaptersModal.addChapters") }}
          </button>
        </div>
        <div class="time">
          <span :class="{ 'duration-active': currentTime > 0 }">{{
            formattedTime.current
          }}</span>
          <span class="separator">/</span>
          <span>{{ formattedTime.duration }}</span>
        </div>
        <div class="pack-buttons">
          <tippy arrow="true" placement="top" a11y="false" interactive="false">
            <template #trigger>
              <button
                class="undo-redo-btn"
                :disabled="currentHistoryIndex <= 0"
                @click="undo"
              >
                <UndoIcon />{{ $t("create.step2.addChaptersModal.undo") }}
              </button>
            </template>
            <div class="shortcut-tooltip">
              <div class="icon-line">
                <AppleIcon />
                <p>cmd + z</p>
              </div>
              <div class="icon-line">
                <MicrosoftIcon />
                <p>ctrl + z</p>
              </div>
            </div>
          </tippy>
          <tippy arrow="true" placement="top" a11y="false" interactive="false">
            <template #trigger>
              <button
                class="undo-redo-btn"
                :disabled="currentHistoryIndex >= historyStack.length - 1"
                @click="redo"
              >
                <RedoIcon />{{ $t("create.step2.addChaptersModal.redo") }}
              </button>
            </template>
            <div class="shortcut-tooltip">
              <div class="icon-line">
                <AppleIcon />
                <p>cmd + shift + z</p>
              </div>
              <div class="icon-line">
                <MicrosoftIcon />
                <p>ctrl + y</p>
              </div>
            </div>
          </tippy>
        </div>
      </div>

      <div class="timeline-container">
        <div
          ref="timelineWrapper"
          class="timeline-wrapper"
          @mousedown="startDragging"
          @mousemove="handleDragging"
          @mouseup="stopDragging"
          @mouseleave="stopDragging"
        >
          <div class="time-ruler">
            <div
              v-for="(marker, index) in timeMarkers"
              :key="index"
              class="time-marker"
              :style="{ left: marker.position + 'px' }"
            >
              {{ marker.time }}
            </div>
          </div>
          <div ref="timeline" class="timeline">
            <template v-if="chapters.length > 0">
              <div
                v-for="(segment, index) in timelineSegments"
                :key="'segment-' + index"
                class="timeline-segment"
                :style="segmentStyle(segment)"
              >
                <div
                  v-if="index !== 0"
                  v-tippy="{ placement: 'top', arrow: true }"
                  class="chapter-divider"
                  :content="$t('create.step2.addChaptersModal.delete')"
                  @click.stop="deleteChapter(index - 1)"
                >
                  <div class="delete-btn"><TrashIcon /></div>
                </div>
              </div>
            </template>
            <div
              v-for="(chapter, index) in chapters"
              :key="'chapter-' + index"
              class="chapter-marker"
              :style="chapterStyle(chapter)"
            ></div>
            <img
              v-for="(frame, index) in frames"
              :key="index"
              class="frame"
              :src="frame"
              :style="{ width: frameWidth + 'px' }"
            />
            <div
              class="selection-line"
              :style="{ transform: `translateX(${selectionLinePosition}px)` }"
              @mousedown.prevent="startLineDragging"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </small-modal>
</template>

<script>
import SmallModal from "@/components/shared/SmallModal.vue";
import SquaredCross from "@/assets/svg/squared-cross-icon.svg?inline";
import SquaredCheck from "@/assets/svg/squared-check-big.svg?inline";
import TrashIcon from "@/assets/svg/trash-icon.svg?inline";
import SplitIcon from "@/assets/svg/split-icon.svg?inline";
import PlayIcon from "@/assets/svg/play-icon-no-bg.svg?inline";
import PauseIcon from "@/assets/svg/pause-icon.svg?inline";
import UndoIcon from "@/assets/svg/undo-icon.svg?inline";
import RedoIcon from "@/assets/svg/redo-icon.svg?inline";
import Mute from "@/assets/svg/mute-icon.svg?inline";
import Unmute from "@/assets/svg/sound-off.svg?inline";
import AppleIcon from "@/assets/svg/apple-icon.svg?inline";
import MicrosoftIcon from "@/assets/svg/microsoft-icon.svg?inline";

export default {
  components: {
    SmallModal,
    SquaredCross,
    SquaredCheck,
    TrashIcon,
    SplitIcon,
    PlayIcon,
    PauseIcon,
    UndoIcon,
    RedoIcon,
    Mute,
    Unmute,
    AppleIcon,
    MicrosoftIcon,
  },
  props: {
    video: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      frames: [],
      videoDuration: 0,
      currentTime: 0,
      frameInterval: 1,
      chapters: this.video.chapters ? [...this.video.chapters] : [],
      selectionLinePosition: 0,
      frameWidth: 48,
      isDragging: false,
      isLineDragging: false,
      timelineOffset: 0,
      wasPlaying: false,
      isPlaying: false,
      timelineScrollWidth: 0,
      historyStack: [],
      currentHistoryIndex: -1,
      maxHistorySteps: 15,
      isMuted: false,
      videoBlob: null,
      videoBlobUrl: null,
      renderedTime: 0,
      displayTime: 0,
      animationId: null,
      worker: null,
      framesGenerated: false,
    };
  },
  computed: {
    formattedTime() {
      return {
        current: this.formatTime(this.currentTime),
        duration: this.formatTime(this.videoDuration),
      };
    },
    timeMarkers() {
      if (!this.videoDuration || !this.$refs.timeline) return [];
      const timelineWidth = this.$refs.timeline.scrollWidth;
      const totalMarkers = 10;
      const interval = this.videoDuration / (totalMarkers - 1);
      const markers = [];

      const effectiveWidth = timelineWidth - 26;
      const startOffset = 13;

      for (let i = 0; i < totalMarkers; i++) {
        const time = i * interval;
        let position =
          (time / this.videoDuration) * effectiveWidth + startOffset;

        if (i === totalMarkers - 1) {
          position = timelineWidth - startOffset;
        }

        markers.push({
          time: this.formatMarkerTime(time),
          position: position,
          isLast: i === totalMarkers - 1,
        });
      }
      return markers;
    },
    chapterStyle() {
      return (chapterTime) => {
        if (!this.$refs.timeline || !this.videoDuration) return {};
        const timelineWidth = this.$refs.timeline.scrollWidth;
        return {
          left: `${(chapterTime / this.videoDuration) * timelineWidth}px`,
          width: "2px",
        };
      };
    },
    sortedChapters() {
      return [...this.chapters].sort((a, b) => a - b);
    },

    timelineSegments() {
      if (this.chapters.length === 0 || !this.videoDuration) return [];

      const points = [0, ...this.sortedChapters, this.videoDuration];

      return points.slice(0, -1).map((start, i) => ({
        start,
        end: points[i + 1],
      }));
    },
    chapterWidth() {
      return this.timelineSegments.length > 0
        ? 100 / this.timelineSegments.length
        : 0;
    },
  },
  watch: {
    "video._id": {
      immediate: true,
      handler() {
        this.frames = [];
        this.videoBlob = null;
        this.videoBlobUrl = null;
        this.framesGenerated = false;
        this.fetchBlobAndGenerate();
      },
    },
    currentTime(newTime) {
      if (!this.isDragging && !this.isLineDragging) {
        this.updateSelectionLine();
      }
    },
  },
  async mounted() {
    if (this.video?.trimmedClipStart) {
      this.currentTime = this.video.trimmedClipStart;
    }
    this.$nextTick(() => {
      this.$refs.timelineWrapper.style.width =
        this.$refs.timeline.scrollWidth + "px";
    });
    this.setupResizeObserver();
    this.historyStack = [[...this.chapters]];
    this.currentHistoryIndex = 0;
    this.startAnimationLoop();
    this.setupWorker();
    this.setupKeyboardListeners();
  },
  beforeUnmount() {
    if (this.videoBlobUrl) {
      URL.revokeObjectURL(this.videoBlobUrl);
    }
    cancelAnimationFrame(this.animationId);
    this.worker.terminate();
    this.removeKeyboardListeners();
  },
  methods: {
    async generateFrames() {
      if (this.framesGenerated) return;
      this.framesGenerated = true;
      try {
        if (!this.videoBlob) return;

        const videoURL = this.videoBlobUrl;
        const tempVideo = document.createElement("video");
        tempVideo.src = videoURL;

        await new Promise((resolve) => {
          tempVideo.addEventListener("loadedmetadata", resolve);
        });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const aspectRatio = tempVideo.videoWidth / tempVideo.videoHeight;

        canvas.height = 80;
        canvas.width = canvas.height * aspectRatio;

        this.frames = [];
        const totalFrames = 20;
        const duration = tempVideo.duration;
        this.videoDuration = duration;
        const frameInterval = duration / (totalFrames - 1);

        for (let i = 0; i < totalFrames; i++) {
          const time = i * frameInterval;
          await new Promise((resolve) => {
            tempVideo.currentTime = time;
            tempVideo.addEventListener(
              "seeked",
              () => {
                ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
                this.frames.push(canvas.toDataURL("image/jpeg", 0.7));
                resolve();
              },
              { once: true }
            );
          });
        }

        this.$forceUpdate();
      } catch (error) {
        console.error(error);
      }
    },

    updateCurrentTime() {
      if (!this.$refs.videoPlayer || this.isDragging || this.isLineDragging)
        return;

      this.currentTime = this.$refs.videoPlayer.currentTime;

      if (this.$refs.videoPlayer.paused) {
        this.renderedTime = this.currentTime;
        this.selectionLinePosition =
          (this.renderedTime / this.videoDuration) * this.timelineScrollWidth;
      }
    },

    updateSelectionLine() {
      const timeline = this.$refs.timeline;
      if (!timeline || !this.videoDuration) return;
      const position =
        (this.currentTime / this.videoDuration) * timeline.scrollWidth;
      this.selectionLinePosition = position;
    },

    startLineDragging(e) {
      this.wasPlaying = !this.$refs.videoPlayer.paused;
      this.$refs.videoPlayer.pause();
      this.isLineDragging = true;
      this.timelineOffset =
        this.$refs.timelineWrapper.getBoundingClientRect().left;
      this.handleDrag(e);
    },

    startDragging(e) {
      this.wasPlaying = !this.$refs.videoPlayer.paused;
      this.$refs.videoPlayer.pause();
      this.isDragging = true;
      this.timelineOffset =
        this.$refs.timelineWrapper.getBoundingClientRect().left;
      this.handleDrag(e);
    },

    handleDragging(e) {
      if (this.wasPlaying) {
        this.$refs.videoPlayer.play();
      }
      if (this.isDragging || this.isLineDragging) {
        this.handleDrag(e);
      }
    },

    handleDrag(e) {
      const wrapper = this.$refs.timelineWrapper;
      const timeline = this.$refs.timeline;

      const rect = wrapper.getBoundingClientRect();
      const scrollLeft = wrapper.scrollLeft;

      let posX = e.clientX - rect.left + scrollLeft;
      posX = Math.max(0, Math.min(posX, timeline.scrollWidth));

      const time = (posX / timeline.scrollWidth) * this.videoDuration;
      this.$refs.videoPlayer.currentTime = time;
      this.currentTime = time;
      this.selectionLinePosition = posX;
    },

    stopDragging() {
      this.isDragging = false;
      this.isLineDragging = false;

      window.removeEventListener("mousemove", this.handleDragging);
      window.removeEventListener("mouseup", this.stopDragging);
    },

    closeModal() {
      this.$emit("close");
    },
    togglePlay() {
      if (this.$refs.videoPlayer.paused) {
        this.$refs.videoPlayer.play();
      } else {
        this.$refs.videoPlayer.pause();
      }
    },

    handlePlay() {
      this.isPlaying = true;
      const update = () => {
        if (!this.$refs.videoPlayer.paused) {
          this.currentTime = this.$refs.videoPlayer.currentTime;
          requestAnimationFrame(update);
        }
      };
      requestAnimationFrame(update);
    },

    handlePause() {
      this.isPlaying = false;
      this.$refs.videoPlayer.pause();
    },

    handleEnded() {
      this.isPlaying = false;
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = (seconds % 60).toFixed(2);
      return `${String(mins).padStart(2, "0")}:${secs.padStart(5, "0")}`;
    },
    formatMarkerTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${String(minutes).padStart(2, "0")}:${String(
        remainingSeconds
      ).padStart(2, "0")}`;
    },
    addChapter() {
      const newChapter = parseFloat(this.currentTime.toFixed(2));

      if (!this.chapters.includes(newChapter)) {
        const newChapters = [...this.chapters, newChapter].sort(
          (a, b) => a - b
        );
        this.updateHistory(newChapters);
        this.chapters = newChapters;
      }
    },

    deleteChapter(index) {
      const newChapters = this.chapters.filter((_, i) => i !== index);
      this.updateHistory(newChapters);
      this.chapters = newChapters;
    },

    segmentStyle(segment) {
      if (!this.videoDuration || this.timelineScrollWidth === 0) return {};

      const startPosition =
        (segment.start / this.videoDuration) * this.timelineScrollWidth;
      let endPosition =
        (segment.end / this.videoDuration) * this.timelineScrollWidth;

      if (segment.end === this.videoDuration) {
        endPosition -= 2;
      }

      return {
        left: `${startPosition}px`,
        width: `${endPosition - startPosition}px`,
        right: "-2px",
      };
    },
    setupResizeObserver() {
      const timelineEl = this.$refs.timeline;
      if (!timelineEl) return;

      const resizeObserver = new ResizeObserver((entries) => {
        this.timelineScrollWidth = entries[0].target.scrollWidth;
      });

      resizeObserver.observe(timelineEl);
    },
    updateHistory(newChapters) {
      this.historyStack = this.historyStack.slice(
        0,
        this.currentHistoryIndex + 1
      );
      this.historyStack.push(newChapters);
      this.currentHistoryIndex++;

      if (this.historyStack.length > this.maxHistorySteps) {
        this.historyStack.shift();
        this.currentHistoryIndex--;
      }
    },
    undo() {
      if (this.currentHistoryIndex > 0) {
        this.currentHistoryIndex--;
        this.chapters = [...this.historyStack[this.currentHistoryIndex]];
      }
    },

    redo() {
      if (this.currentHistoryIndex < this.historyStack.length - 1) {
        this.currentHistoryIndex++;
        this.chapters = [...this.historyStack[this.currentHistoryIndex]];
      }
    },
    toggleMute() {
      this.$refs.videoPlayer.muted = !this.isMuted;
      this.isMuted = !this.isMuted;
    },
    getChapterFill(segment) {
      const current = this.currentTime;
      const start = segment.start;
      const end = segment.end;

      if (current <= start) return 0;
      if (current >= end) return 100;
      return ((current - start) / (end - start)) * 100;
    },
    async saveChapters() {
      try {
        await this.$store.dispatch("video/updateVideoChapters", {
          videoId: this.video._id,
          chapters: this.sortedChapters,
        });
        this.closeModal();
      } catch (error) {
        console.error("Error saving chapters:", error);
      }
    },
    setupWorker() {
      this.worker = new Worker(
        URL.createObjectURL(
          new Blob(
            [
              `
      let video = null;
      let lastFrame = performance.now();
      
      self.addEventListener('message', (e) => {
        if (e.data.type === 'INIT') {
          video = e.data.video;
          update();
        }
      });
      
      function update() {
        if (!video) return;
        
        const now = performance.now();
        if (now - lastFrame >= 8) {
          postMessage({
            currentTime: video.currentTime,
            duration: video.duration
          });
          lastFrame = now;
        }
        requestAnimationFrame(update);
      }
    `,
            ],
            { type: "application/javascript" }
          )
        )
      );

      this.worker.onmessage = (e) => {
        if (this.isDragging || this.isLineDragging) return;

        this.currentTime = e.data.currentTime;
        this.videoDuration = e.data.duration;

        if (Math.abs(this.currentTime - this.renderedTime) > 0.1) {
          this.renderedTime = this.currentTime;
        }
      };
    },

    startAnimationLoop() {
      const animate = () => {
        if (!this.$refs.videoPlayer || this.isDragging || this.isLineDragging) {
          this.animationId = requestAnimationFrame(animate);
          return;
        }

        const delta = this.currentTime - this.renderedTime;
        this.renderedTime += delta * 0.45;
        this.selectionLinePosition = Math.max(
          0,
          Math.min(
            (this.renderedTime / this.videoDuration) * this.timelineScrollWidth,
            this.timelineScrollWidth
          )
        );

        this.animationId = requestAnimationFrame(animate);
      };
      this.animationId = requestAnimationFrame(animate);
    },
    setupKeyboardListeners() {
      this.keydownListener = this.handleKeydown.bind(this);
      window.addEventListener("keydown", this.keydownListener);
    },

    removeKeyboardListeners() {
      window.removeEventListener("keydown", this.keydownListener);
    },

    handleKeydown(e) {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;

      if (e.key === " ") {
        e.preventDefault();
        this.togglePlay();
      }

      if (["ArrowLeft", "ArrowRight"].includes(e.key)) {
        this.handleArrowKeys(e);
      }

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === "z") {
        e.preventDefault();
        this.undo();
      } else if (
        (e.ctrlKey && e.key === "y") ||
        (e.metaKey && e.shiftKey && e.key === "z")
      ) {
        e.preventDefault();
        this.redo();
      }
    },

    handleArrowKeys(e) {
      e.preventDefault();
      if (!this.videoDuration) return;

      const timeDelta = e.key === "ArrowLeft" ? -0.01 : 0.01;
      let newTime = this.currentTime + timeDelta;

      newTime = Math.max(0, Math.min(newTime, this.videoDuration));

      if (this.isPlaying) {
        this.$refs.videoPlayer.pause();
        this.isPlaying = false;
      }

      this.$refs.videoPlayer.currentTime = newTime;
      this.currentTime = newTime;
      this.selectionLinePosition =
        (newTime / this.videoDuration) * this.timelineScrollWidth;
    },
    async fetchBlobAndGenerate() {
      try {
        const response = await fetch(this.video.url);
        this.videoBlob = await response.blob();
        this.videoBlobUrl = URL.createObjectURL(this.videoBlob);
        await this.generateFrames();
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style lang="scss">
.chapters-modal .small-modal.bigger {
  width: 1000px;
  max-width: 1000px;
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
.modal-background {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

.header-buttons {
  display: flex;
  gap: 16px;
  align-items: center;
}

.header-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(123, 1, 181, 1);
  color: white;
  border: 1px solid rgba(123, 1, 181, 1);
  border-radius: 2px;

  &:hover {
    border: 1px solid rgba(123, 1, 181, 1);
    background: white;
    color: rgba(123, 1, 181, 1);
    svg {
      path {
        fill: rgba(123, 1, 181, 1);
      }
    }
  }
}

.back {
  background: white;
  border: 1px solid rgba(196, 196, 196, 1);
  color: rgba(196, 196, 196, 1);
  box-sizing: border-box;
}

.squared-cross {
  path {
    fill: rgba(196, 196, 196, 1);
  }
}
.timeline-container {
  height: 130px;
  margin-bottom: 24px;
  padding: 0 12px;
  position: relative;
}
.timeline-wrapper {
  position: relative;
  height: 100%;
}
.time-ruler {
  position: absolute;
  top: 0px;
  left: 0;
  right: 0;
  height: 12px;
  color: $dark-grey;
  font-size: 12px;
  pointer-events: none;
  border-top: 1px solid $light-grey;
  overflow: visible;
  margin-bottom: 12px;
  z-index: 2;
}

.time-marker {
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  padding: 2px;
  white-space: nowrap;
  @include base-font;
  font-size: 8px;
  color: $light-grey;
  border-left: 1px solid $light-grey;

  &::after {
    content: "";
    position: absolute;
    bottom: -3px;
    left: 50%;
    width: 1px;
    height: 5px;
    background: white;
  }
}

.timeline {
  display: flex;
  height: 80px;
  background: #333;
  position: relative;
  align-items: center;
  top: 30px;
  z-index: 1;

  .frame {
    height: 80px;
    width: 48px;
    object-fit: cover;
    object-position: center;
    flex-shrink: 0;
    border-right: 1px solid #444;
    display: block;
  }
}

.selection-line {
  position: absolute;
  top: -13px;
  bottom: -2px;
  width: 3px;
  background: rgba(123, 1, 181, 1);
  cursor: col-resize;
  border-top: 3px solid rgba(123, 1, 181, 1);
  z-index: 20;
  transition: none;
  transform: translate3d(0, 0, 0);
  will-change: transform;
  backface-visibility: hidden;
  transform-style: preserve-3d;

  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 12px solid rgba(123, 1, 181, 1);
  }

  &::after {
    content: "";
    position: absolute;
    top: -19px;
    left: 50%;
    transform: translateX(-50%);
    width: 12px;
    height: 8px;
    background: rgba(123, 1, 181, 1);
    border-radius: 0;
  }

  &:hover {
    width: 5px;
    border-top: 5px solid rgba(123, 1, 181, 1);
    top: -16px;

    &::before {
      border-left-width: 8px;
      border-right-width: 8px;
    }

    &::after {
      width: 15.5px;
    }
  }
}

.time-display {
  text-align: center;
  padding: 10px;
  font-weight: bold;
  color: #fff;
}

.actions {
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 16px;
}

.pack-buttons {
  display: flex;
  gap: 8px;

  button {
    @include base-font;
    padding: 6px 12px;
    background: rgba(123, 1, 181, 1);
    color: white;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    border: 1px solid rgba(123, 1, 181, 1);
  }
}

button:hover {
  background: white;
  border: 1px solid rgba(123, 1, 181, 1);
  color: rgba(123, 1, 181, 1);
  svg {
    path {
      fill: rgba(123, 1, 181, 1);
    }
  }
}

.video-container {
  display: flex;
  width: 100%;
  justify-content: center;
  height: 350px;
  background-color: rgba(217, 217, 217, 1);
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;
}
.video-wrapper {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  margin-inline: auto;      
  min-width: 0;              
  min-height: 0;
}

.video-player {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.mute-button {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 10;
  background: rgba(60, 60, 60, 0.6);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    width: 30px;
    height: 30px;
    background: rgba(60, 60, 60, 0.6);
    border: none;
    path {
      fill: white;
    }
  }
}

.video-editor-header {
  padding: 16px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  @include base-font;
  @include font-big;
  font-weight: 600;
}
.time {
  @include base-font;
  @include font-smaller;
  display: flex;
  gap: 4px;
  align-items: center;

  .duration-active {
    color: $dark-purple;
    display: inline-block;
    min-width: 60px;
    font-feature-settings: "tnum";
    font-variant-numeric: tabular-nums;
  }
}
.chapter-marker {
  position: absolute;
  top: 2px;
  bottom: 2px;
  background: rgba($dark-purple, 0.2);
  pointer-events: none;
  z-index: 1;
}

.timeline-segment {
  position: absolute;
  top: -1px;
  bottom: 0;
  background: rgba(100, 77, 237, 0.1);
  border: 3px solid $dark-purple;
  border-radius: 4px;
  z-index: 500;
  height: 79px;
}

.chapter-divider {
  position: absolute;
  left: -2px;
  top: 0px;
  bottom: 0px;
  width: 3px;
  background: $dark-purple;
  cursor: pointer;
  transition: all 0.2s;
}

.delete-btn {
  position: absolute;
  left: -9px;
  top: 45%;
  transform: translateY(-50%);
  background: white;
  color: white;
  width: 22px;
  height: 22px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.progress-container {
  position: absolute;
  top: 5px;
  left: 10px;
  right: 10px;
  z-index: 10;
  height: 2px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.chapters-container {
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;
  border-radius: 2px;
}

.chapter {
  height: 2px;
  background: rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.chapter-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #fff;
  transition: width 0.1s linear;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  will-change: width;
}
.shortcut-tooltip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.icon-line {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
